import React, { useEffect, useState } from 'react';

export interface DATE_INPUT_PROPS {
  id: string;
  label: string;
  onDateChangeFn: (
    e: React.ChangeEvent<HTMLInputElement>,
    index?: number
  ) => void;
  onBlurFn: (e: React.FocusEvent<HTMLInputElement>) => void;
  value: string | undefined;
  showLabel?: boolean;
  type?: string;
  required?: boolean;
  disabled?: boolean;
  minDateLimit?: string;
  maxDateLimit?: string;
  isGreaterThanToday?: boolean;
  isLesserThanToday?: boolean;
  greaterThanDateComponent?: string;
  lesserThanDateComponent?: string;
  hidden?: boolean;
  formData?: Record<string, string | number>;
  index?: number;
}

export const DateInput: React.FC<DATE_INPUT_PROPS> = ({
  id,
  label,
  onDateChangeFn,
  onBlurFn,
  value = '',
  showLabel = true,
  type = 'date',
  required = false,
  disabled = false,
  hidden = false,
  minDateLimit,
  maxDateLimit,
  isGreaterThanToday = false,
  isLesserThanToday = false,
  greaterThanDateComponent,
  lesserThanDateComponent,
  formData,
  index = 0,
}) => {
  const [inputValue, setInputValue] = useState<string>(value);
  const [dateRangeError, setDateRangeError] = useState<string>('');

  useEffect(() => {
    validateDate(inputValue); // Validate initially and on formData changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, formData]);

  const validateDate = (newValue: string) => {
    let error = '';
    const dateValue = new Date(newValue);
    if (greaterThanDateComponent) {
      const greaterDateValue = formData?.[greaterThanDateComponent] || '';
      if (
        typeof greaterDateValue === 'string' &&
        greaterDateValue &&
        dateValue <= new Date(greaterDateValue)
      ) {
        error = `Date should be greater than ${formatDateToDDMMYYYY(
          greaterDateValue
        )}`;
      }
    }
    if (lesserThanDateComponent) {
      const lesserDateValue = formData?.[lesserThanDateComponent] || '';
      if (
        typeof lesserDateValue === 'string' &&
        lesserDateValue &&
        dateValue >= new Date(lesserDateValue)
      ) {
        error = `Date should be lesser than ${formatDateToDDMMYYYY(
          lesserDateValue
        )}`;
      }
    }
    if (isGreaterThanToday && dateValue <= new Date()) {
      error = 'Date should be greater than today';
    } else if (isLesserThanToday && dateValue >= new Date()) {
      error = 'Date should be lesser than today';
    }
    setDateRangeError(error);
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    validateDate(newValue);
    if (index) {
      onDateChangeFn(e, index);
    } else {
      onDateChangeFn(e);
    }
  };

  return (
    <div className={`pb-5 sm:pb-4 ${hidden ? 'hidden' : ''}`}>
      <div className="flex items-center justify-between">
        <label
          htmlFor={id}
          className={`text-base font-medium sm:text-s-14 mb-[6px] ${
            showLabel ? '' : 'hidden'
          }`}
        >
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      </div>
      <div className="flex relative">
        <input
          id={id}
          type={type}
          className="border border-grey-300 rounded-sm text-base w-full h-12 pl-2 placeholder-gray-400 placeholder-opacity-75"
          name={id}
          value={inputValue}
          onChange={handleDateChange}
          onBlur={onBlurFn}
          min={minDateLimit}
          max={maxDateLimit}
          disabled={disabled}
        />
      </div>
      {dateRangeError && (
        <p className="text-red-350 text-base font-medium inline-block">
          {dateRangeError}
        </p>
      )}
    </div>
  );
};

function formatDateToDDMMYYYY(dateStr: string) {
  const date = new Date(dateStr);
  const day = ('0' + date.getDate()).slice(-2);
  const month = ('0' + (date.getMonth() + 1)).slice(-2);
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}
